// Classifies errors thrown while signing/sending transactions so the UI can
// tell a deliberate wallet cancellation apart from a genuine failure, and never
// surfaces a raw stringified exception to the user.

export interface WalletErrorInfo {
  /** True when the user declined the transaction in their wallet. */
  isRejection: boolean;
  /** A clean, user-facing message. */
  message: string;
}

// Wallets signal a user rejection in a few ways: an EIP-1193-style numeric code
// (4001), or a message on the error or its nested provider error.
const REJECTION_PATTERN =
  /user rejected|rejected the request|user denied|user cancell?ed|request rejected|declined the transaction|approval denied/i;

const asRecord = (value: unknown): Record<string, any> | null =>
  typeof value === "object" && value !== null ? (value as Record<string, any>) : null;

export const isUserRejection = (err: unknown): boolean => {
  const root = asRecord(err);
  if (!root) return false;

  // Some wallet adapters wrap the provider error under `.error`.
  const nested = asRecord(root.error);
  const code = root.code ?? nested?.code;
  if (code === 4001 || code === "4001") return true;

  const messages = [root.message, nested?.message].filter(
    (message): message is string => typeof message === "string"
  );
  return messages.some((message) => REJECTION_PATTERN.test(message));
};

// A failed program instruction reaches the UI as an opaque code, and which
// shape depends on where the transaction died: `custom program error: 0x1780`
// from a failed preflight, `{"Custom":6016}` from a confirmed failure, or
// Anchor's own log lines. Codes we have copy for are mapped here; anything else
// keeps falling through to the raw message.
//
// Two constraints on anything added here:
//
// 1. Anchor error codes are per-program and they collide — 6016 is
//    VaultUnderwater in drip_vault_v2 but TimestampsNotInFuture in merky. Only
//    the drip vault's errors reach this helper today (useAirdropClaim builds its
//    own message), and the matchers below key on the bare number, so before
//    routing another program's errors through here, scope the match by program.
// 2. The message is shown by every caller, including the withdraw and
//    reward-token paths, so it must describe the vault's state rather than
//    promise anything about the action the user was taking.
const PROGRAM_ERRORS: { code: number; name: string; message: string }[] = [
  {
    code: 6016,
    name: "VaultUnderwater",
    message: "Deposits are temporarily closed while the vault rate recovers.",
  },
];

// Anchored to a surrounding token so a bare number elsewhere in the message
// (an amount, a slot) can never be mistaken for an error code.
const PROGRAM_ERROR_MATCHERS = PROGRAM_ERRORS.map(({ code, name, message }) => ({
  message,
  pattern: new RegExp(
    [
      `custom program error:\\s*0x${code.toString(16)}\\b`,
      `"Custom"\\s*:\\s*${code}\\b`,
      `Error Number:\\s*${code}\\b`,
      `Error Code:\\s*${name}\\b`,
    ].join("|"),
    "i"
  ),
}));

// Program logs live on the error itself or on the adapter's nested provider
// error; both are worth reading, since some wallets keep only a generic message.
const errorTexts = (err: unknown): string[] => {
  const root = asRecord(err);
  if (!root) return [];

  const nested = asRecord(root.error);
  const logs = [root.logs, nested?.logs].flatMap((value) => (Array.isArray(value) ? value : []));
  return [root.message, nested?.message, ...logs].filter(
    (text): text is string => typeof text === "string"
  );
};

const describeProgramError = (err: unknown): string | null => {
  const texts = errorTexts(err);
  const match = PROGRAM_ERROR_MATCHERS.find(({ pattern }) =>
    texts.some((text) => pattern.test(text))
  );
  return match?.message ?? null;
};

export const describeWalletError = (err: unknown, fallback: string): WalletErrorInfo => {
  if (isUserRejection(err)) {
    return { isRejection: true, message: "You cancelled the transaction in your wallet." };
  }

  const programError = describeProgramError(err);
  if (programError) {
    return { isRejection: false, message: programError };
  }

  const root = asRecord(err);
  const rawMessage = typeof root?.message === "string" ? root.message.trim() : "";
  return { isRejection: false, message: rawMessage.length > 0 ? rawMessage : fallback };
};

/** Convenience for hooks that only need a clean message to throw. */
export const getWalletErrorMessage = (err: unknown, fallback: string): string =>
  describeWalletError(err, fallback).message;
